// <purpose>ONE shared hover/focus text tooltip, replacing native title= across the triage
// surfaces (chips/pills, disabled-reason buttons, colour-strip bars). Operator ruling (Part A
// (WIM two-drafts, 2026-08) Task 11 Part 2): lift the app's existing navy/stx-fadeIn info-bubble chrome (the same
// palette FirstRunTooltip already renders — theme.colors.navy/white, theme.borderRadius.md, the
// existing stx-fadeIn keyframe) into one component rather than inventing new visual language.
// Fix round 1 (reviewer findings): shadow is theme.shadows.cardHover — the SAME token
// DashboardSeite's own hover box already uses for this exact "floating info bubble on hover"
// role (DashboardSeite.tsx :578); its resting-card cousins (cardLifted/cardLiftedHover) are a
// different semantic (a static BentoCard's own boundary, not a transient overlay), so they were
// not the match. z-index is theme.zIndex.tooltip — no layering token existed before this
// component, so ONE was added beside spacing/borderRadius in theme.ts rather than hand-rolling
// another ad-hoc literal (the app's z-index range today is 100-1200, all unshared literals;
// tooltip keeps the previous 9999 value — always above everything else — as a named token now).
// Positioning mechanics (rect-based fixed placement, viewport clamp, above/below flip) mirror the
// clamp math already proven in FirstRunTooltip and DashboardSeite's Verlauf-row hover box — a
// third hand-rolled scheme was not invented for this.</purpose>
// <dependencies>theme.ts tokens only (colors.navy/white, shadows.cardHover, zIndex.tooltip,
// borderRadius.md, spacing, font.family) and the existing stx-fadeIn keyframe — no new
// colours/animations/z-index literals.</dependencies>
// <constraints>UI Layout Stability: fix round 1 removed the display:inline-flex wrapper span that
// used to hold the anchor — it was becoming the row's OWN flex item at call sites like
// AntwortenSeite/DashboardSeite's colour strip, so the strip's flexShrink:0 stopped governing the
// row directly. The anchor (a host element — span/button/div, true for all ten current call
// sites) now takes the ref/handlers/aria props directly via cloneElement, and stays the exact DOM
// node it always was; the bubble itself is portaled to document.body (position:fixed regardless,
// so the portal only matters for a11y-tree/paint-order cleanliness, not positioning). A child that
// cannot safely take a ref (a Fragment, or a function component not designed to forward one) falls
// back to a display:contents wrapper, which is excluded from the box/flex model entirely — same
// non-interference guarantee, for the cases cloneElement can't reach directly. None of the ten
// real call sites hit that fallback (all pass host elements); it exists for API generality only.
// aria-describedby is present ONLY while the bubble is actually mounted (visible && rect, the same
// gate the bubble's own render uses) — fix round 1: an unconditional aria-describedby pointed at
// an id that only sometimes existed in the DOM, a dangling reference in the default (unhovered)
// state on all ten sites. aria-label is added ONLY when the anchor renders no text of its own
// (measured via textContent), preserving today's accessible name for anchors that already have
// visible text. The bubble repositions on scroll/resize while visible (capture:true on scroll so
// a nested scrollable ancestor, not only the window, is caught) rather than freezing at its
// original rect. FOCUS-TRIGGERING IS REAL (fix wave M8): an anchor the browser does not put in
// the tab order on its own — a span or div with no tabIndex, which is what most call sites pass
// (chips, pills, colour strips) — is given tabIndex={0}, so onFocus/onBlur actually fire and the
// text is keyboard-reachable. A natively focusable anchor (button/input/select/textarea, or an
// `a` with an href), and any anchor that declares its own tabIndex (a deliberate -1 included),
// is left exactly as authored.</constraints>
import React, { useState, useRef, useLayoutEffect, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../theme';

// THE hover delay (house rule, small facts on a row 2026-10-19, registry entry tooltips_and_motion):
// a bubble waits 450 ms before it shows, so a pointer crossing a row of pills never flashes a
// bubble per pill. Implemented as the bubble's OWN animation delay (mounted at once, painted
// after the wait): leaving before the wait unmounts it unseen, assistive tech sees the
// describedby immediately, and no timer state is kept per anchor.
export const TOOLTIP_DELAY_MS = 450;

interface InfoTooltipProps {
  // Plain text — same content that used to live in title=, moved not reworded. Always the
  // bubble's accessible reading, even when `content` draws something richer.
  text: string;
  // Optional rich content (the second-axis stack: pills, not prose). The bubble then wears no
  // navy chrome — the content brings its own — but keeps the role, the id, the placement and the
  // delay, so a reveal is one mechanism on every surface.
  content?: React.ReactNode;
  // Single anchor element (span/button/div) — cloned to receive ref/handlers/aria-describedby/
  // aria-label directly when it's a host element (all ten current call sites); see <constraints>.
  children: React.ReactElement;
  placement?: 'below' | 'above';
  // Hover-only (part 5-2, 2026-10-02): focus does NOT open the bubble. For an anchor the surface
  // focuses PROGRAMMATICALLY as part of its own flow (a draft strip handed focus after a compose or
  // an outcome), where a bubble popping on every handoff is noise. Use only where the anchor's own
  // accessible name already carries what the clerk needs; the hover is a supplement.
  hoverOnly?: boolean;
}

const SAFE_MARGIN = 8;
const MAX_WIDTH = 280;
// Rough single/two-line estimate used only to decide whether the bubble fits below the anchor
// before flipping above — same approximation approach FirstRunTooltip (100) and DashboardSeite
// (120) already use for their own flip decisions, scaled down for this component's shorter text.
const EST_HEIGHT = 40;

// Host elements the browser puts in the tab order on their own. Everything else (span, div, the
// chips and colour strips most of this component's call sites anchor on) is unreachable by
// keyboard, so onFocus/onBlur would never fire and the tooltip would be hover-ONLY — mouse-only
// discoverability is exactly what converting away from native title= was supposed to end.
const NATIVELY_FOCUSABLE = new Set(['button', 'input', 'select', 'textarea']);

type AnchorHandlerProps = {
  tabIndex?: number;
  href?: string;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  children?: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
};

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ text, content, children, placement = 'below', hoverOnly = false }) => {
  const [visible, setVisible] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [hasOwnText, setHasOwnText] = useState(true);
  const anchorRef = useRef<HTMLElement | null>(null);
  const reactId = useId().replace(/:/g, '');
  const tooltipId = `stx-tooltip-${reactId}`;

  const childProps = children.props as AnchorHandlerProps;
  // Host elements (span/button/div — every one of the ten current call sites) accept a plain DOM
  // ref via cloneElement; a Fragment or a bare function component might not (see <constraints>).
  const isHost = typeof children.type === 'string';

  // Dependency key: the anchor's own PRIMITIVE text children, so the layout effect below only
  // re-measures the DOM (and only re-renders via setHasOwnText) when that text actually changes —
  // not on every unrelated parent re-render (fix round 1, MINOR).
  const childTextKey = React.Children.toArray(childProps.children)
    .map(c => (typeof c === 'string' || typeof c === 'number') ? String(c) : '')
    .join('|');

  useLayoutEffect(() => {
    setHasOwnText((anchorRef.current?.textContent ?? '').trim().length > 0);
  }, [childTextKey]);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    // The fallback wrapper (a function-component anchor such as a split pill) is `display: contents`
    // and has no box of its own; its first element child is the anchor that does.
    const box = el.firstElementChild && getComputedStyle(el).display === 'contents' ? el.firstElementChild : el;
    setRect(box.getBoundingClientRect());
  }, []);
  const show = useCallback(() => { measure(); setVisible(true); }, [measure]);
  const hide = useCallback(() => setVisible(false), []);

  // Reposition while visible if the page scrolls/resizes underneath the anchor (fix round 1,
  // MINOR) — capture:true on scroll also catches scrolling inside a nested scrollable ancestor
  // (e.g. a list panel), not only the window itself.
  useEffect(() => {
    if (!visible) return;
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [visible, measure]);

  const setRef = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node;
    const existingRef = childProps.ref;
    if (typeof existingRef === 'function') existingRef(node);
    else if (existingRef && typeof existingRef === 'object') {
      (existingRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlers = {
    onMouseEnter: (e: React.MouseEvent) => { show(); childProps.onMouseEnter?.(e); },
    onMouseLeave: (e: React.MouseEvent) => { hide(); childProps.onMouseLeave?.(e); },
    onFocus: (e: React.FocusEvent) => { if (!hoverOnly) show(); childProps.onFocus?.(e); },
    onBlur: (e: React.FocusEvent) => { hide(); childProps.onBlur?.(e); },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') hide();
      childProps.onKeyDown?.(e);
    },
  };
  // aria-describedby ONLY while the bubble is actually in the DOM (fix round 1, CRITICAL) — never
  // a dangling id reference in the anchor's default (unhovered/unfocused) state.
  const ariaProps = {
    ...(visible ? { 'aria-describedby': tooltipId } : {}),
    ...(hasOwnText ? {} : { 'aria-label': text }),
  };

  // Make focus-triggering REAL on anchors the browser would not focus (fix wave M8): a span/div
  // with no tabIndex of its own gets tabIndex={0}. An anchor that is natively focusable, or that
  // already declares its own tabIndex (including a deliberate -1), is left exactly as authored.
  const anchorTag = isHost ? (children.type as string) : null;
  const nativelyFocusable = anchorTag !== null
    && (NATIVELY_FOCUSABLE.has(anchorTag) || (anchorTag === 'a' && childProps.href !== undefined));
  const focusProps = !nativelyFocusable && childProps.tabIndex === undefined ? { tabIndex: 0 } : {};

  const anchor = isHost
    ? React.cloneElement(children, { ref: setRef, ...handlers, ...ariaProps, ...focusProps } as Partial<unknown>)
    : (
      // Fallback, unexercised by any of the ten current call sites — see <constraints>.
      <span ref={setRef as React.Ref<HTMLSpanElement>} style={{ display: 'contents' }} {...handlers} {...ariaProps} {...focusProps}>
        {children}
      </span>
    );

  let box: React.ReactNode = null;
  if (visible && rect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const fitsBelow = placement === 'below' && rect.bottom + 6 + EST_HEIGHT <= vh - SAFE_MARGIN;
    const top = fitsBelow ? rect.bottom + 6 : undefined;
    const bottom = fitsBelow ? undefined : Math.max(SAFE_MARGIN, vh - rect.top + 6);
    const maxLeft = Math.max(SAFE_MARGIN, vw - MAX_WIDTH - SAFE_MARGIN);
    const left = Math.max(SAFE_MARGIN, Math.min(rect.left, maxLeft));
    const chrome: React.CSSProperties = content === undefined
      ? {
        padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
        background: theme.colors.navy,
        color: theme.colors.white,
        borderRadius: theme.borderRadius.md,
        boxShadow: theme.shadows.cardHover,
        fontSize: 12,
        lineHeight: 1.4,
        whiteSpace: 'normal',
      }
      : {};
    box = (
      <span
        role="tooltip"
        id={tooltipId}
        aria-label={content === undefined ? undefined : text}
        style={{
          position: 'fixed',
          top, bottom, left,
          maxWidth: Math.min(MAX_WIDTH, vw - 2 * SAFE_MARGIN),
          fontFamily: theme.font.family,
          zIndex: theme.zIndex.tooltip,
          pointerEvents: 'none',
          // `backwards` holds the first keyframe (opacity 0) through the delay — see TOOLTIP_DELAY_MS.
          animation: `stx-fadeIn 0.15s ease ${TOOLTIP_DELAY_MS}ms backwards`,
          ...chrome,
        }}
      >
        {content === undefined ? text : content}
      </span>
    );
  }

  return (
    <>
      {anchor}
      {box && createPortal(box, document.body)}
    </>
  );
};

export default InfoTooltip;
